import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { ImportIds, ImportService } from "../../../services/ImportService";
import RequestService from "../../../services/RequestService";
import {
  formatDateTimeDigits,
  formatDateTimeLong
} from "../../../utils/FormatUtils";
import ModalService from "../../shared/bootstrap/modal/Modal";
import ToastContext from "../../shared/bootstrap/Toast";
import { ImportLogListResponse } from "../../shared/entities/ImportLogListResponse";
import FloatingPanelService from "../../shared/FloatingPanel";
import { TranslationService } from "../../../services/TranslationService";
import TableContext from "../../task/TableContext";
import { ImportTableContextValues } from "./ImportLog";
import ImportLogDetail from "./ImportLogDetail";
import CompanyService from "../../../services/CompanyService";
import TooltipComponent from "../../shared/TooltipComponent";
import { useHistory } from "react-router-dom";
import { SafeHtml } from "../../../components/SafeHtml";
import DictionaryService from "../../../services/DictionaryService";
import ImportLogPausedDetail from "./ImportLogPausedDetail";

const ImportLogItem = ({ data }: { data: ImportLogListResponse.Item }) => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const { reload, isFirstImport } = useContext(
    TableContext
  ) as ImportTableContextValues;
  const status =
    !data.Status || data.Status.length === 0
      ? undefined
      : TranslationService.getTranslation(data.Status);
  const importType = ImportService.findImportType(
    data.Type,
    data.UpdateStrategy
  );
  const timezone = CompanyService.getTimezone();
  const lastStatusDate = useMemo(() => {
    if (data.LastStatus) {
      return new Date(
        new Date(data.LastStatus).getTime() - timezone * 60 * 1000
      );
    }
    return null;
  }, [data.LastStatus, timezone]);
  const history = useHistory();
  const [downloading, setDownloading] = useState(false);
  const [dictionaryName, setDictionaryName] = useState<string | null>(null);

  const getResult = (data: ImportLogListResponse.Item) => {
    let resultData =
      data.IOCount > 0
        ? `<a href="/${TranslationService.currentLanguage}/report/iolist?impid=${data.ImportID}">` +
          (data.IOCount.toString() +
            " " +
            translate.Invoice +
            (data.IOCount !== 1 ? "s" : "")) +
          "</a>"
        : "";
    resultData +=
      data.PersonCount > 0
        ? `<a href="/${TranslationService.currentLanguage}/client?impid=${data.ImportID}">` +
          ((resultData ? ", " : "") +
            data.PersonCount.toString() +
            " " +
            translate.Client +
            (data.PersonCount !== 1 ? "s" : "")) +
          "</a>"
        : "";
    resultData +=
      data.TransactionCount > 0
        ? `<a href="/${TranslationService.currentLanguage}/report/payment?impid=${data.ImportID}">` +
          ((resultData ? ", " : "") +
            data.TransactionCount.toString() +
            " " +
            translate.TransactionSingular +
            (data.TransactionCount !== 1 ? "s" : "")) +
          "</a>"
        : "";
    // eslint-disable-next-line
    resultData +=
      data.MessageCount > 0
        ? `<a href="/${TranslationService.currentLanguage}/report/reportactivity?impid=${data.ImportID}">` +
          ((resultData ? ", " : "") +
            data.MessageCount.toString() +
            " " +
            translate.Comment +
            (data.MessageCount !== 1 ? "s</a>" : ""))
        : "";
    resultData +=
      data.TagCount > 0
        ? (!resultData ? ", " : "") +
          data.TagCount.toString() +
          " " +
          translate.Tag +
          (data.TagCount !== 1 ? "s" : "")
        : "";
    resultData +=
      data.GroupCount > 0
        ? (!resultData ? ", " : "") +
          data.GroupCount.toString() +
          " " +
          translate.Group +
          (data.GroupCount !== 1 ? "s" : "")
        : "";
    return resultData;
  };

  const showImportDetail = useCallback(() => {
    if (data.Paused) {
      FloatingPanelService.showPanel({
        title: translate.PredecessorValidation,
        children: <ImportLogPausedDetail data={data} />,
        width: 900,
        height: 700
      });
      return;
    }

    data.ControlSuspended
      ? history.push(`import/finish?id=${data.ImportID}`)
      : FloatingPanelService.showPanel({
          title: translate.Import,
          children: <ImportLogDetail data={data} />,
          width: 900,
          height: 700
        });
  }, [data, history, translate.Import, translate.PredecessorValidation]);

  const showImport = () =>
    FloatingPanelService.showPanel({
      title: translate.Import,
      children: <ImportLogDetail data={data} />,
      width: 900,
      height: 700
    });

  const deleteImport = (id: number) => {
    const importItem = data;
    const message =
      importItem.total === 0
        ? translate.AreYouSureYouWishToDeleteThisImportRecord
        : translate.AreYouSureYouWishToDeleteThisImportRecordWithData ||
          translate.AreYouSureYouWishToDeleteThisImportRecord;

    const onDelete = async () => {
      const result = await ImportService.delete(id);
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        return;
      }

      // Manejar respuestas específicas del servidor
      const response = result as { Response?: string };
      if (response.Response === "Ok") {
        showToast(
          translate.ProcessRunning || "Proceso en ejecución",
          undefined,
          "info"
        );
      } else if (response.Response === "Fail-Pending") {
        showToast(
          translate.ProcessPending || "Proceso pendiente",
          undefined,
          "warning"
        );
      } else if (response.Response === "Deleted") {
        reload();
      } else if (response.Response === "Fail") {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
      } else {
        // Si no hay respuesta específica, recargar
        reload();
      }
    };
    ModalService.showDefaultModal({
      title: translate.DeleteImport,
      message: message,
      acceptButtonLabel: translate.Delete,
      acceptButtonClassName: "btn btn-danger",
      onAcceptClick: onDelete
    });
  };

  const downloadImport = async () => {
    // Restricción de fecha: solo archivos después de 2013-08-10
    const createdDate = data.Created ? new Date(data.Created) : null;
    const minDate = new Date(2013, 7, 10); // Agosto es mes 7 (0-indexed)

    if (createdDate && createdDate <= minDate) {
      showToast(
        translate.FileNotAvailableForDownload ||
          "Archivo no disponible para descarga",
        undefined,
        "warning"
      );
      return;
    }

    setDownloading(true);
    try {
      await RequestService.downloadFile(
        `/import/download?id=${data.ImportID}`,
        data.Filename
      );
    } catch (error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
    } finally {
      setDownloading(false);
    }
  };

  const autoFtp =
    data.ImportTemplate !== null &&
    (data.ImportTemplate?.entity === ImportIds.AutomaticInvoiceUpdate ||
      data.ImportTemplate?.entity === ImportIds.AutomaticCustomerUpdate);

  // Cargar nombre de diccionario si es necesario
  useEffect(() => {
    const loadDictionaryName = async () => {
      if (
        data.ImportTemplate?.entity === ImportIds.Dictionary &&
        data.ImportTemplateID
      ) {
        const dictionaries = await DictionaryService.getList();
        if (!(dictionaries instanceof Error)) {
          const dic = dictionaries.list.find(
            (x) => x.ImportTemplateID === data.ImportTemplateID
          );
          if (dic) {
            setDictionaryName(dic.Name);
          }
        }
      }
    };
    loadDictionaryName();
  }, [data.ImportTemplate?.entity, data.ImportTemplateID]);

  // Función para generar enlace a plantilla según entity
  const getTemplateLink = (): string | null => {
    if (!data.ImportTemplate || !data.ImportTemplateID) return null;

    const entity = data.ImportTemplate.entity;
    const tid = data.ImportTemplateID;
    const lang = TranslationService.currentLanguage;

    switch (entity) {
      case ImportIds.Comments: // Activity = 3
        return `/${lang}/ImportTemplateManual?entityId=3&tid=${tid}`;
      case ImportIds.OutstandingInvoicesList: // IO = 0
        return `/${lang}/ImportTemplateManual?entityId=0&tid=${tid}`;
      case ImportIds.UpdateMultipleInvoices: // IOUpdate = 5
        return `/${lang}/ImportTemplateManual?entityId=5&tid=${tid}`;
      case ImportIds.NewInvoices: // NewIO = 8
        return `/${lang}/ImportTemplateManual?entityId=8&tid=${tid}`;
      case ImportIds.AutomaticInvoiceUpdate: // IOAutomaticUpdate = 6
        return `/${lang}/ImportTemplateManual?entityId=6&tid=${tid}`;
      case ImportIds.AutomaticCustomerUpdate: // PersonAutomaticUpdate = 9
        return `/${lang}/ImportTemplateManual?entityId=9&tid=${tid}`;
      case ImportIds.Client: // Person = 1
        return `/${lang}/ImportTemplateManual?entityId=1&tid=${tid}`;
      case ImportIds.Dictionary: // Dictionary = 11
        return `/${lang}/ImportTemplateManual?entityId=11&tid=${tid}`;
      default:
        return null;
    }
  };

  const templateLink = getTemplateLink();
  const displayName = dictionaryName || data.ImportTemplate?.Name || "";

  const statusDescription = useMemo(() => {
    if (!(data.Paused ?? false)) {
      if (data.ControlSuspended) {
        return translate.ImportSuspended;
      } else {
        // Si hay una traducción directa del status y es diferente al original, usarla
        // Esto maneja casos como "Reading" -> "Leyendo datos de Intiza"
        const translatedStatus =
          status && status !== data.Status ? status : null;
        if (translatedStatus) {
          return translatedStatus;
        }

        switch ((data.Status ?? "").toLocaleLowerCase()) {
          case "completed":
            return translate.Complete;
          case "person":
            return translate.Clients;
          case "group":
            return translate.Groups;
          case "io":
            return translate.Invoice + "s";
          case "transaction":
            return translate.Payment + "s";
          case "activity":
            return translate.Comments;
          case "io updates":
            return translate.IOUpdating;
          case "io-add":
            return translate.IOAdd;
          case "start":
            return translate.Started;
          case "processed":
            return translate.processed;
          case "fallo":
            return translate.Failed;
          case "import":
            return translate.StartingImport;
          case "processing data":
            return translate.Processing;
          case "fail-dates":
            return translate["Fail-Dates"];
          case "fail-repeat":
            return translate["fail-repeat"];
          case "fail-all":
            return translate["fail-all"];
          case "fail-overlapping":
            return translate.FailOverlapping;
          case "empty-file":
            return translate.EmptyFile;
          case "separator-error":
            return translate.SeparatorError;
          case "formulas":
            return translate.Calulating;
          case "formulas calculated":
            return "Fórmulas calculadas";
          case "tags":
            return translate.Tags;
          case "canceled":
            return "Cancelado";
          case "resume":
            return "Pendiente de finalización";
          case "decrypting":
            return "Desencriptando archivo";
          case "unziping":
            return "Descomprimiendo archivo";
          case "saving templated":
            return "Grabando archivo";
          case "processing group":
            return "Procesando grupos";
          case "validating additionals":
            return "Validando adicionales";
          case "validating updates":
            return "Validando actualizaciones";
          case "invoices":
            return translate.Invoices;
          case "transactions":
            return translate.Payment + "s";
          default:
            if (/^log-error-\d+$/.test((data.Status ?? "").toLowerCase())) {
              const nro = (data.Status ?? "").match(/\d+/)?.[0] ?? "";
              return `Error al procesar logs: sólo ${nro} fueron procesados correctamente.`;
            } else if ((data.Status ?? "").toLowerCase().includes("template")) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("template", translate.Template);
            } else if (
              (data.Status ?? "").toLowerCase().includes("creating-file")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("creating-file", "Creando archivo");
            } else if (
              (data.Status ?? "").toLowerCase().includes("dictionary")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("dictionary", translate.Dictionary);
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading data 0%")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading data 0%", translate["Reading Data 0%"]);
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading data")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading data", translate["Reading Data"]);
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading file 0%")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading file 0%", translate["Reading File 0%"]);
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading file")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading file", translate["Reading File"]);
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading person")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading", translate.ReadingData)
                .replace("person", "(" + translate.Clients + ")");
            } else if (
              (data.Status ?? "").toLowerCase().includes("reading io")
            ) {
              return (
                (data.Status ?? "")
                  .toLowerCase()
                  .replace("reading", translate.ReadingData)
                  .replace("io", "(" + translate.Invoice + "s") + ")"
              );
            } else if ((data.Status ?? "").toLowerCase().includes("reading")) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("reading", translate.ReadingData);
            } else if (
              (data.Status ?? "").toLowerCase().includes("person-update")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("person-update", translate.UpdatingCustomers);
            } else if (
              (data.Status ?? "").toLowerCase().includes("invoices udpate")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("invoices udpate", translate.UpdateInvoices);
            } else if (
              (data.Status ?? "").toLowerCase().includes("validating new ios")
            ) {
              return (data.Status ?? "")
                .toLowerCase()
                .replace("validating new ios", "Validando nuevas facturas");
            } else {
              return data.Status ?? "";
            }
        }
      }
    } else {
      return translate.InPause;
    }
  }, [data.ControlSuspended, data.Paused, data.Status, translate, status]);

  // Construir el tooltip completo como en v1: description + porcentaje + fecha completa
  const isLogError = /^log-error-\d+$/.test((data.Status ?? "").toLowerCase());

  const statusTitle = useMemo(() => {
    let tooltip = statusDescription;
    if (
      !isLogError &&
      data.Percentage !== undefined &&
      data.Percentage !== null
    ) {
      tooltip += " " + Math.floor(data.Percentage).toString() + "%";
    }
    if (lastStatusDate) {
      tooltip += " " + formatDateTimeLong(lastStatusDate);
    }
    return tooltip;
  }, [statusDescription, data.Percentage, lastStatusDate, isLogError]);

  const showDelete = () => {
    if (CompanyService.isBackOfficeUser()) {
      return true;
    }

    const showFirstImport = isFirstImport === data.ImportID;
    let show =
      (CompanyService.canDo("importdata") &&
        (showFirstImport || data.total === 0)) ||
      CompanyService.getIsAdmin();

    if (data.ImportID !== isFirstImport && data.total > 0) {
      show = false;
    }

    return show;
  };

  return (
    <tr className="show-child-on-hover">
      <td width={100}>
        {importType && TranslationService.getTranslation(importType.text)}
      </td>
      <td width={100}>
        {(data.Started ?? data.Created)
          ? formatDateTimeDigits(
              new Date(
                new Date(data.Started ?? data.Created).getTime() -
                  timezone * 60 * 1000
              )
            )
          : ""}
      </td>
      <td width={100}>
        {lastStatusDate ? formatDateTimeDigits(lastStatusDate) : ""}
      </td>

      <td title={statusTitle} onClick={showImportDetail} className="pointer">
        <TooltipComponent title={statusTitle}>
          {statusDescription}
        </TooltipComponent>
      </td>
      <td>
        <SafeHtml html={getResult(data)} />
      </td>
      <td>
        <TooltipComponent
          title={autoFtp ? "AUTO" : data.FTP ? "FTP" : data.UserName || ""}
        >
          {autoFtp ? "AUTO" : data.FTP ? "FTP" : data.UserName}
        </TooltipComponent>
      </td>
      <td>
        <TooltipComponent title={displayName || ""}>
          {templateLink ? (
            <a
              href={templateLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                history.push(templateLink);
              }}
            >
              {displayName}
            </a>
          ) : (
            displayName
          )}
        </TooltipComponent>
      </td>
      <td width={100}>
        <TooltipComponent title={data.Filename || ""}>
          {data.Filename}
        </TooltipComponent>
      </td>
      <td>
        {downloading ? (
          <i className="show-when-hovering-parent fas fa-spinner-third fa-spin third pointer"></i>
        ) : (
          <i
            className="show-when-hovering-parent fa-regular fa-arrow-down-to-line pointer"
            onClick={downloadImport}
          ></i>
        )}
        <i
          className={
            "pointer fa-regular fa-search show-when-hovering-parent mx-3"
          }
          onClick={showImport}
        />
        {showDelete() && (
          <i
            className={
              "pointer fa-regular fa-trash-can text-danger show-when-hovering-parent"
            }
            onClick={() => deleteImport(data.ImportID)}
          />
        )}
      </td>
    </tr>
  );
};
export default ImportLogItem;
